<template>
  <v-stepper-content step="2" class="px-0 pt-4 pb-0">
    <v-form ref="formInner">
      <!-- Seção: Dados pessoais -->
      <section class="ds-form-section">
        <div class="ds-form-section__head">
          <div class="ds-form-section__title">
            <v-icon small color="primary" class="mr-2">mdi-account-outline</v-icon>
            <span>Dados pessoais</span>
          </div>
        </div>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              :value="user.name"
              label="Nome completo"
              prepend-inner-icon="mdi-account-outline"
              clearable
              outlined
              dense
              hide-details="auto"
              color="primary"
              :rules="[(v) => !!v || 'Nome é obrigatório']"
              @input="updateUserField('name', $event)"
            />
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              :value="user.email"
              label="Email"
              prepend-inner-icon="mdi-email-outline"
              clearable
              outlined
              dense
              hide-details="auto"
              color="primary"
              type="email"
              :rules="emailRules"
              @input="updateUserField('email', $event)"
            />
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <v-text-field
              :value="user.cpf"
              label="CPF"
              prepend-inner-icon="mdi-card-account-details-outline"
              clearable
              outlined
              dense
              hide-details="auto"
              color="primary"
              readonly
              v-mask="'###.###.###-##'"
              @input="updateUserField('cpf', $event)"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-select
              :value="user.gender"
              :items="genderOptions"
              label="Gênero"
              prepend-inner-icon="mdi-human-male-female"
              clearable
              outlined
              dense
              hide-details="auto"
              color="primary"
              :rules="[(v) => !!v || 'Obrigatório']"
              @change="updateUserField('gender', $event)"
            />
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <v-text-field
              :value="user.birth_date"
              label="Data de Nascimento"
              prepend-inner-icon="mdi-calendar"
              type="date"
              clearable
              outlined
              dense
              hide-details="auto"
              color="primary"
              :rules="[(v) => !!v || 'Obrigatório']"
              @input="updateUserField('birth_date', $event)"
            />
          </v-col>

          <v-col v-if="targetUserType === 'doctor'" cols="12" sm="6" md="4">
            <v-text-field
              :value="user.crm"
              label="CRM"
              prepend-inner-icon="mdi-medical-bag"
              outlined
              dense
              hide-details="auto"
              color="primary"
              :rules="[(v) => !!v || 'CRM é obrigatório']"
              clearable
              @input="updateUserField('crm', $event)"
            />
          </v-col>
        </v-row>
      </section>

      <!-- Seção: Menor de idade -->
      <section
        v-if="
          !isEditMode && targetUserType === 'patient' && isMinorBlockVisible
        "
        class="ds-form-section"
        data-tour="np-menor"
      >
        <div class="ds-form-section__head">
          <div class="ds-form-section__title">
            <v-icon small color="primary" class="mr-2">mdi-account-child-outline</v-icon>
            <span>Paciente menor de idade</span>
          </div>
        </div>

        <div class="d-flex align-center justify-space-between">
          <p v-if="!isExternalGuardian" class="caption grey--text mb-0">
            Idade detectada: <b>{{ patientAge }}</b> anos
          </p>
          <v-spacer v-else />

          <v-switch
            v-model="internalMinorEnabled"
            :disabled="isExternalGuardian"
            inset
            color="primary"
            label="Menor de idade"
            class="ma-0"
            hide-details
            @change="$emit('update:is-minor-enabled', $event)"
          />
        </div>

        <div v-if="isMinorEnabled" class="mt-3">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-if="!isExternalGuardian"
                :value="guardianCpfInput"
                label="CPF do responsável"
                outlined
                dense
                hide-details="auto"
                clearable
                v-mask="'###.###.###-##'"
                :rules="guardianCpfRules"
                @input="$emit('update:guardian-cpf-input', $event)"
              />
            </v-col>

            <v-col cols="12" sm="6" class="d-flex align-center">
              <v-btn
                v-if="!isExternalGuardian"
                color="primary"
                rounded
                depressed
                class="mr-2"
                :loading="guardianLoading"
                :disabled="guardianLoading"
                @click="$emit('search-guardian')"
              >
                Buscar responsável
              </v-btn>

              <v-btn
                v-if="guardianChecked"
                color="grey"
                outlined
                rounded
                @click="$emit('reset-guardian')"
              >
                Limpar
              </v-btn>
            </v-col>
          </v-row>

          <v-alert v-if="guardianError" type="error" class="mt-2 mb-0" dense>
            {{ guardianError }}
          </v-alert>

          <v-alert
            v-if="guardianChecked && guardianExists"
            type="success"
            class="mt-2 mb-0"
            dense
          >
            Responsável encontrado: <b>{{ guardianName }}</b>
          </v-alert>

          <v-alert
            v-if="guardianChecked && !guardianExists && !guardianError"
            type="warning"
            class="mt-2 mb-0"
            dense
          >
            Nenhum responsável encontrado com esse CPF.
          </v-alert>

          <!-- CPF não existe: oferece cadastrar o responsável na hora -->
          <div v-if="guardianNotFound && !isExternalGuardian" class="mt-2">
            <v-btn
              color="primary"
              rounded
              depressed
              @click="$emit('register-guardian')"
            >
              <v-icon left>mdi-account-plus</v-icon>
              Cadastrar responsável agora
            </v-btn>
          </div>
        </div>
      </section>

      <!-- Seção: Endereço -->
      <section class="ds-form-section">
        <div class="ds-form-section__head">
          <div class="ds-form-section__title">
            <v-icon small color="primary" class="mr-2">mdi-map-marker-outline</v-icon>
            <span>Endereço</span>
            <span class="ds-form-section__optional">opcional</span>
          </div>
        </div>

        <v-progress-linear
          v-if="loadingCEP"
          indeterminate
          color="primary"
          class="mb-3"
        />

        <v-row dense>
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              :value="user.address.cep"
              label="CEP"
              prepend-inner-icon="mdi-map-search-outline"
              outlined
              dense
              hide-details="auto"
              clearable
              color="primary"
              :rules="cepRules"
              maxlength="9"
              v-mask="'#####-###'"
              @input="updateAddressField('cep', $event)"
              @blur="$emit('buscar-cep')"
            />
          </v-col>

          <v-col cols="12" sm="8" md="6">
            <v-text-field
              :value="user.address.street"
              label="Logradouro"
              prepend-inner-icon="mdi-road-variant"
              outlined
              dense
              hide-details="auto"
              clearable
              color="primary"
              maxlength="60"
              @input="updateAddressField('street', $event)"
            />
          </v-col>

          <v-col cols="12" sm="4" md="3">
            <v-text-field
              :value="user.address.number"
              label="Número"
              prepend-inner-icon="mdi-numeric"
              outlined
              dense
              hide-details="auto"
              clearable
              color="primary"
              maxlength="6"
              v-mask="'######'"
              @input="updateAddressField('number', $event)"
            />
          </v-col>

          <v-col cols="12" sm="8" md="4">
            <!-- ✅ Task 1: Complemento -->
            <v-text-field
              :value="user.address.complement"
              label="Complemento (opcional)"
              prepend-inner-icon="mdi-home-plus-outline"
              outlined
              dense
              hide-details="auto"
              clearable
              color="primary"
              maxlength="100"
              placeholder="Ex: Apto 202, Bloco B"
              @input="updateAddressField('complement', $event)"
            />
          </v-col>

          <v-col cols="12" sm="5" md="3">
            <v-text-field
              :value="user.address.neighborhood"
              label="Bairro"
              prepend-inner-icon="mdi-home-city-outline"
              outlined
              dense
              hide-details="auto"
              clearable
              color="primary"
              @input="updateAddressField('neighborhood', $event)"
            />
          </v-col>

          <v-col cols="12" sm="4" md="3">
            <v-text-field
              :value="user.address.city"
              label="Cidade"
              prepend-inner-icon="mdi-city"
              outlined
              dense
              hide-details="auto"
              clearable
              color="primary"
              @input="updateAddressField('city', $event)"
            />
          </v-col>

          <v-col cols="12" sm="3" md="2">
            <v-text-field
              :value="user.address.state"
              label="Estado"
              prepend-inner-icon="mdi-map"
              outlined
              dense
              hide-details="auto"
              clearable
              color="primary"
              maxlength="2"
              @input="updateAddressField('state', $event)"
            />
          </v-col>
        </v-row>
      </section>

      <!-- Seção: Clínica -->
      <section v-if="shouldShowClinicSelection" class="ds-form-section">
        <div class="ds-form-section__head">
          <div class="ds-form-section__title">
            <v-icon small color="primary" class="mr-2">mdi-hospital-building</v-icon>
            <span>Clínica</span>
          </div>
        </div>

        <v-select
          :value="selectedClinics"
          :items="clinics"
          item-text="name"
          item-value="id"
          label="Clínicas"
          multiple
          chips
          outlined
          dense
          hide-details="auto"
          color="primary"
          :rules="[(v) => v.length > 0 || 'Selecione ao menos uma clínica']"
          :disabled="clinics.length === 1"
          @change="$emit('update:selected-clinics', $event)"
        >
          <template v-slot:item="{ item, attrs, on }">
            <v-list-item v-bind="attrs" v-on="on">
              <v-list-item-action>
                <v-checkbox
                  :input-value="selectedClinics.includes(item.id)"
                  color="primary"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  CNPJ: {{ item.cnpj }} - {{ item.city }}/{{ item.state }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 3" small>{{ item.name }}</v-chip>
            <span v-else-if="index === 3" class="grey--text caption">
              +{{ selectedClinics.length - 3 }} outras
            </span>
          </template>
        </v-select>
      </section>

      <!-- Seção: Telefones -->
      <section class="ds-form-section">
        <div class="ds-form-section__head">
          <div class="ds-form-section__title">
            <v-icon small color="primary" class="mr-2">mdi-phone-outline</v-icon>
            <span>Telefones</span>
          </div>
          <v-btn
            class="white--text"
            small
            color="primary"
            rounded
            depressed
            :disabled="user.phones.length >= 3"
            @click="$emit('add-phone')"
          >
            <v-icon small left>mdi-plus</v-icon>
            Adicionar
          </v-btn>
        </div>

        <v-row
          v-for="(telefone, index) in user.phones"
          :key="index"
          dense
        >
          <v-col cols="12" sm="5">
            <v-text-field
              :value="telefone.number"
              :label="`Telefone ${index + 1}`"
              prepend-inner-icon="mdi-phone"
              clearable
              outlined
              dense
              hide-details="auto"
              color="primary"
              :rules="[(v) => !!v || 'Número obrigatório']"
              v-mask="'(##) #####-####'"
              @input="updatePhoneField(index, 'number', $event)"
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              :value="telefone.type"
              :items="phoneTypes"
              label="Tipo"
              clearable
              outlined
              dense
              hide-details="auto"
              color="primary"
              :rules="[(v) => !!v || 'Tipo obrigatório']"
              @change="updatePhoneField(index, 'type', $event)"
            />
          </v-col>
          <v-col cols="9" sm="2" class="d-flex align-center">
            <v-checkbox
              :input-value="telefone.is_primary"
              label="WhatsApp"
              dense
              color="primary"
              class="mt-0 pt-0"
              hide-details
              @change="$emit('define-main-phone', index)"
            />
          </v-col>
          <v-col cols="3" sm="1" class="d-flex align-center justify-end">
            <v-btn
              v-if="user.phones.length > 1"
              icon
              color="red"
              @click="$emit('remove-phone', index)"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </section>

      <!-- Seção: Como soube da clínica -->
      <section v-if="targetUserType === 'patient'" class="ds-form-section">
        <div class="ds-form-section__head">
          <div class="ds-form-section__title">
            <v-icon small color="primary" class="mr-2">mdi-account-network-outline</v-icon>
            <span>Como soube da clínica?</span>
          </div>
        </div>
        <v-radio-group
          :value="selectedReferralType"
          name="referral_source"
          row
          color="primary"
          class="mt-0 pt-0"
          hide-details="auto"
          @change="$emit('handle-referral-change', $event)"
        >
          <v-radio label="Google" value="Google" />
          <v-radio label="Instagram" value="Instagram" />
          <v-radio
            label="Indicação de um amigo"
            value="Indicação de um amigo"
          />
          <v-radio label="Médico" value="Médico" />
          <v-radio label="Outro" value="Outro" />
        </v-radio-group>

        <v-text-field
          v-if="selectedReferralType === 'Indicação de um amigo'"
          :value="friendReferralName"
          label="Nome do amigo"
          outlined
          dense
          hide-details="auto"
          clearable
          color="primary"
          class="mt-3"
          :rules="[(v) => !!v || 'Informe o nome do amigo']"
          @input="$emit('update:friend-referral-name', $event)"
        />

        <v-text-field
          v-if="selectedReferralType === 'Médico'"
          :value="doctorReferralName"
          label="Nome do médico"
          outlined
          dense
          hide-details="auto"
          clearable
          color="primary"
          class="mt-3"
          :rules="[(v) => !!v || 'Informe o nome do médico']"
          @input="$emit('update:doctor-referral-name', $event)"
        />

        <v-text-field
          v-if="selectedReferralType === 'Outro'"
          :value="customReferralSource"
          label="Por favor, especifique"
          outlined
          dense
          hide-details="auto"
          clearable
          color="primary"
          class="mt-3"
          :rules="[(v) => !!v || 'Informe como soube do sistema']"
          @input="$emit('update:custom-referral-source', $event)"
        />
      </section>

      <v-alert v-if="erro" type="error" class="mt-3 mb-0" dense>{{ erro }}</v-alert>
    </v-form>

    <!-- Os botões moram dentro da etapa (não no rodapé do diálogo): o
         rodapé só existe na etapa 2. -->
    <v-card-actions class="px-0 pt-4 pb-0">
      <v-btn
        outlined
        color="primary"
        :disabled="loading"
        @click="$emit('go-back')"
      >
        <v-icon left>mdi-arrow-left</v-icon>
        Voltar
      </v-btn>

      <v-spacer />

      <v-btn
        outlined
        color="primary"
        @click="$emit('close')"
      >
        <v-icon left>mdi-close</v-icon>
        Cancelar
      </v-btn>

      <v-btn
        color="primary"
        depressed
        :loading="loading"
        dark
        @click="$emit('save-user')"
      >
        <v-icon left>mdi-content-save</v-icon>
        Salvar
      </v-btn>
    </v-card-actions>
  </v-stepper-content>
</template>

<script>
export default {
  name: "AddNewUserDialogFormStep",

  props: {
    user: { type: Object, required: true },
    emailRules: { type: Array, required: true },
    genderOptions: { type: Array, required: true },
    phoneTypes: { type: Array, required: true },
    targetUserType: { type: String, required: true },
    isEditMode: { type: Boolean, default: false },
    isUnder18: { type: Boolean, default: false },
    patientAge: { type: Number, default: 0 },
    isMinorEnabled: { type: Boolean, default: false },
    guardianCpfInput: { type: String, default: "" },
    guardianCpfRules: { type: Array, default: () => [] },
    guardianLoading: { type: Boolean, default: false },
    guardianChecked: { type: Boolean, default: false },
    guardianExists: { type: Boolean, default: false },
    guardianName: { type: String, default: "" },
    guardianError: { type: String, default: "" },
    guardianNotFound: { type: Boolean, default: false },
    loadingCEP: { type: Boolean, default: false },
    shouldShowClinicSelection: { type: Boolean, default: false },
    clinics: { type: Array, default: () => [] },
    selectedClinics: { type: Array, default: () => [] },
    selectedReferralType: { type: String, default: "" },
    friendReferralName: { type: String, default: "" },
    doctorReferralName: { type: String, default: "" },
    customReferralSource: { type: String, default: "" },
    erro: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    isExternalGuardian: { type: Boolean, default: false },
  },

  data() {
    return {
      internalMinorEnabled: this.isMinorEnabled,
    };
  },

  computed: {
    isMinorBlockVisible() {
      return this.isExternalGuardian || this.isUnder18;
    },
    cepRules() {
      return [
        (v) => !v || /^\d{5}-?\d{3}$/.test(v) || "CEP inválido",
      ];
    },
  },

  watch: {
    isMinorEnabled(val) {
      this.internalMinorEnabled = val;
    },
    isExternalGuardian(val) {
      if (val) this.internalMinorEnabled = true;
    },
  },

  methods: {
    validate() {
      return this.$refs.formInner?.validate() ?? false;
    },
    resetValidation() {
      this.$refs.formInner?.resetValidation?.();
    },
    updateUserField(field, value) {
      this.$emit("update:user", { ...this.user, [field]: value });
    },
    updateAddressField(field, value) {
      this.$emit("update:user", {
        ...this.user,
        address: { ...this.user.address, [field]: value },
      });
    },
    updatePhoneField(index, field, value) {
      const phones = this.user.phones.map((phone, i) =>
        i !== index ? phone : { ...phone, [field]: value },
      );
      this.$emit("update:user", { ...this.user, phones });
    },
  },
};
</script>
